import { useState, useContext, useEffect } from "react";
import _ from 'lodash';
import { Button } from 'react-bootstrap';
import KanbanFunctions from "../../api/kanban-board/kanban-functions";
import { KanbanContext } from './../../pages/kanban-board/kanban-board';
import { MaintenanceKanbanContext } from '../properties/maintenance-tab';
import { SmartInput } from "../reusable-elements/floating-smart-input";
import { notNullOrEmpty } from "../../utils/validators";
import { arrayAny } from "../../utils/arrayUtils";

const SubTaskList = ({ project, tile, parent, toggleSubTaskComplete, allowEdits, updateTile }) => {

    // const checkStatuses = [
    //     { status: "N/A", value: 0 },
    //     { status: "Passed", value: 1 },
    //     { status: "Failed", value: 2 }
    // ];

    // Try to get context from either KanbanContext or MaintenanceKanbanContext
    const kanbanContext = useContext(KanbanContext);
    const maintenanceContext = useContext(MaintenanceKanbanContext);
    const context = kanbanContext || maintenanceContext;
    const { setData, deleteSubTask, addSubTask } = context || {};

    const [state, setState] = useState(() => structuredClone(tile));

    useEffect(() => {
        setState(structuredClone(tile));
    }, [tile.id, tile.subTasks?.length]);

    const completedTasksString = (tasks) => {
        let numberOfTasks = tasks.length;
        let numberOfCompletedTasks = 0;

        if (numberOfTasks > 0) {
            numberOfCompletedTasks = _.filter(tasks, t => t.isCompleted === true).length;
        }
        else {
            return "(N/A)";
        }
        return `(${numberOfCompletedTasks} / ${numberOfTasks})`;
    };

    const completedTasksPercentageString = (tasks) => {
        let numberOfTasks = tasks.length;
        let numberOfCompletedTasks = 0;

        if (numberOfTasks > 0) {
            numberOfCompletedTasks = _.filter(tasks, t => t.isCompleted === true).length;
        }
        else {
            return 100;
        }

        return ((numberOfCompletedTasks / numberOfTasks).toFixed(2)) * 100;
    };

    const localToggleSubTaskComplete = subTaskId => {
        handleStateUpdates(tileInput => {
            const st = tileInput.subTasks.find(st => st.subTaskId === subTaskId);
            st.isCompleted = !st.isCompleted;
            return tileInput;
        });
    };

    const localSetSubTaskCheckCompleteStatus = subTaskId => {

        const subTask = state.subTasks.find(st => st.subTaskId === subTaskId);
        const subTaskIndex = state.subTasks.findIndex(st => st.subTaskId === subTaskId);

        subTask.checkCompleteStatus = subTask.checkCompleteStatus ?? 'na';

        let nextStatusValue = -1;

        switch (subTask.checkCompleteStatus) {
            case 'na':
                nextStatusValue = 'passed';
                break;
            case 'passed':
                nextStatusValue = 'failed';
                break;
            case 'failed':
                nextStatusValue = 'na'; //back to beginning...
                break;
            default:
                throw new Error("Unexpected State");
        }

        subTask.checkCompleteStatus = nextStatusValue;

        state.subTasks.splice(subTaskIndex, 1);
        state.subTasks.splice(subTaskIndex, 0, subTask);

        //setSubTaskCheckCompleteStatus(project, tileId, subTaskId, nextStatusValue);
        updateTile(state);
    }

    const localDeleteSubTask = async (project, tile, subTask) => {
        if (subTask) {
            console.log(`Delete sub-task ${subTask.subTaskId} from tile ${tile.id}`)
            
            // Update local state immediately
            setState(current => {
                const newState = structuredClone(current);
                if (newState.subTasks) {
                    newState.subTasks = newState.subTasks.filter(st => st.subTaskId !== subTask.subTaskId);
                }
                updateTile(newState);
                return newState;
            });
            
            await deleteSubTask(project, tile.id, subTask);
        }
    };

    const localAddSubTask = async (project, tile) => {
        const subTask = { subTaskId: KanbanFunctions.getRandomId(), title: "", order: 0, isCompleted: false, editOn: true, templateItem: false, checkCompleteStatus: 'NA', autoComplete: false };
        
        // Update local state immediately
        setState(current => {
            const newState = structuredClone(current);
            newState.subTasks = newState.subTasks || [];
            newState.subTasks.push(subTask);
            updateTile(newState);
            return newState;
        });
        
        await addSubTask(project, tile.id, subTask);
    }

    const isInspection = () => {
        return tile?.template?.isInspection === true;
    }

    const inspectionStatus = subTaskId => {
        if (state && arrayAny(state.subTasks)) {
            const subTask = state.subTasks.find(st => st.subTaskId === subTaskId);
            subTask.checkCompleteStatus = subTask.checkCompleteStatus ?? 'na';

            return subTask.checkCompleteStatus;
        }
        return 'na';
    }

    const handleStateUpdates = updateMethod => {

        setState(current => {
            const newState = updateMethod(current);
            updateTile(newState);
            return newState;
        });
    };

    return (
        <div className="flex-fill">
            {arrayAny(tile.subTasks) ?
                <>
                    <hr className="mb-10px bg-gray-600" />
                    <div className="d-flex flex-row align-items-center justify-content-between mb-5px"> {/* Flex container  */}
                        <div className="flex-grow-0 fs-12px me-2 text-dark fw-bold">
                            Completed Tasks {completedTasksString(state.subTasks || [])}
                        </div>
                        <div className="flex-grow-1 progress h-5px w-auto mb-0 me-2">
                            <div className="progress-bar progress-bar-striped" style={{ width: `${completedTasksPercentageString(state.subTasks || [])}%` }}></div>
                        </div>
                        <div className="fs-10px fw-bold">{`${completedTasksPercentageString(state.subTasks || [])}%`}</div>
                        <div className="d-flex flex-column ms-10px">
                            {allowEdits ?
                                <Button className="mb-5px btn btn-outline-default text-gray-600 btn-xs rounded-pill fs-10px px-2" variant="light" onClick={() => localAddSubTask(project, tile)}>
                                    Add Task
                                </Button>
                                :
                                <></>
                            }
                            <a href={"#/" + state.id} className="btn btn-outline-default text-gray-600 btn-xs rounded-pill fs-10px px-2" data-bs-toggle="collapse" data-bs-target={`#collapse-${parent}-${state.id}`}>
                                Collapse
                            </a>
                        </div>
                    </div>
                    <div className="form-group mb-1">
                        <div className="collapse show" id={`collapse-${parent}-${state.id}`}>
                            <table key={`task-${parent}-${state.id}`} className="table table-borderless table-sm text-start">

                                <tbody>
                                    {state.subTasks?.map(subTask => (
                                        <tr className="d-flex" key={`subTask-${subTask.subTaskId}`}>
                                            <td style={{alignContent: 'center'}}>
                                                <input name="Completed"  disabled={!allowEdits} type="checkbox" className="form-check-input" id={`task-${parent}-${tile.id}-${subTask.subTaskId}-checked`} onChange={e => localToggleSubTaskComplete(subTask.subTaskId)} checked={subTask.isCompleted} />
                                            </td>
                                            {isInspection() &&
                                                <td style={{alignContent: 'center'}}>
                                                    {inspectionStatus(subTask.subTaskId) === 'na' &&
                                                        < div className="badge bg-default">
                                                            <i key={`project-${project}-tile-${tile.tileId}-subtask-${subTask.subTaskId}`} onClick={() => localSetSubTaskCheckCompleteStatus(subTask.subTaskId)} name="Not Checked or N/A" className="fas fa-lg fa-fw fa-expand text-black-900" />
                                                        </div>
                                                    }
                                                    {inspectionStatus(subTask.subTaskId) === 'passed' &&
                                                        < div className="badge bg-success">
                                                            <i key={`project-${project}-tile-${tile.tileId}-subtask-${subTask.subTaskId}`} onClick={() => localSetSubTaskCheckCompleteStatus(subTask.subTaskId)} name="Not Checked or N/A" className="fas fa-lg fa-fw fa-check" />
                                                        </div>
                                                    }
                                                    {inspectionStatus(subTask.subTaskId) === 'failed' &&
                                                        < div className="badge bg-danger">
                                                            <i key={`project-${project}-tile-${tile.tileId}-subtask-${subTask.subTaskId}`} onClick={() => localSetSubTaskCheckCompleteStatus(subTask.subTaskId)} name="Not Checked or N/A" className="fas fa-lg fa-fw fa-x" />
                                                        </div>
                                                    }
                                                </td>
                                            }
                                            <td className="w-100">

                                                <SmartInput
                                                    label="Description"
                                                    className="fs-12px lh-12 mb-2px fw-bold text-dark w-100"
                                                    text={subTask.title}
                                                    updateCallback={newValue => {
                                                        handleStateUpdates(state => {
                                                            const subTaskToUpdate = state.subTasks.find(st => st.subTaskId === subTask.subTaskId);
                                                            subTaskToUpdate.title = newValue;
                                                            return state;
                                                        })
                                                    }}
                                                    readOnly={false}
                                                    required={true}
                                                    validatorFn={text => notNullOrEmpty(text)}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
                :
                <div className="ms-auto">
                    {allowEdits ?
                        <Button className="btn btn-outline-default text-gray-600 btn-xs rounded-pill fs-10px px-2" variant="light" onClick={e => localAddSubTask(project, tile)}>
                            Add Task
                        </Button>
                        :
                        <></>
                    }
                </div>
            }
        </div >
    );

};

export default SubTaskList;